/**
 * API client for communicating with the Flask backend
 */
import axios from 'axios';
import { markReadOnlyBlock } from '../utils/toast';

const getDefaultApiBase = () => {
  if (process.env.PROD) {
    return process.env.REACT_APP_API_BASE_URL || '/api';
  }
  return 'http://localhost:5000/api';
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || getDefaultApiBase();

// CSRF guard: the backend requires this header on every authenticated write. Set it on the global
// axios too, which several pages and AuthContext use directly.
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 second timeout
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true, // Send cookies for authentication
});

// Export the raw api client for direct use
export { api };

// Request interceptor for logging and adding metadata
api.interceptors.request.use(
  (config) => {
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Session-level errors from the backend that need a page-level reaction, not a per-call one.
const PUBLIC_PATHS = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/pricing', '/terms', '/privacy'];
let sessionRedirecting = false;

const redirectTo = (path, { force = false } = {}) => {
  if (sessionRedirecting) return;
  if (!force && window.location.pathname === path) return;
  sessionRedirecting = true;
  window.location.assign(path);
};

// Returns true when the error was a session message that has been handled (no retry needed).
const handleSessionError = (error) => {
  const status = error.response?.status;
  const message = error.response?.data?.error;
  if (!status || typeof message !== 'string') return false;
  const path = window.location.pathname;

  if (status === 401) {
    if (
      message === 'View-as session expired' ||
      message === 'View-as session ended' ||
      message === 'The customer account is disabled'
    ) {
      // The server already restored the operator session: a full reload to /admin refetches
      // /auth/me and drops every cache from the customer identity. NOT a logout.
      redirectTo('/admin', { force: true });
      return true;
    }
    if (message === 'Session ended - please sign in again') {
      redirectTo('/login');
      return true;
    }
    if (message === 'Unauthorized - Please login') {
      const isPublic = PUBLIC_PATHS.includes(path) || path.startsWith('/unsubscribe/');
      if (!isPublic) redirectTo('/login');
      return true;
    }
    return false;
  }

  if (status === 403) {
    if (message.startsWith('Read-only view:')) {
      // Shown by the view-as banner only; error toasts are suppressed for a moment
      error.viewAsReadOnly = true;
      markReadOnlyBlock();
      window.dispatchEvent(new CustomEvent('view-as-readonly', { detail: message }));
      return true;
    }
    if (message === 'Missing X-Requested-With header') {
      // Should never happen in normal use: show a generic error, no redirect
      const text = 'Request blocked, please refresh the page and try again';
      const event = new CustomEvent('app-request-blocked', { detail: text, cancelable: true });
      // The view-as banner shows it when present; otherwise fall back to an alert
      if (window.dispatchEvent(event)) window.alert(text);
      return true;
    }
    if (message === 'Forbidden - Admin access required') {
      redirectTo('/dashboard');
      return true;
    }
    if (message === 'Super admin accounts cannot use the customer app') {
      if (!path.startsWith('/admin')) redirectTo('/admin');
      return true;
    }
  }
  return false;
};

// Response interceptor with retry logic and error handling
api.interceptors.response.use(
  (response) => {
    // Log successful requests
    const duration = new Date() - response.config.metadata.startTime;
    console.log(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`);
    return response;
  },
  async (error) => {
    const config = error.config;

    // 429 (throttled auth endpoints): no retry, no session/redirect handling; the caller shows the countdown
    if (error.response?.status === 429) {
      return Promise.reject(error);
    }

    // Super admin / view-as session messages (see handleSessionError)
    if (handleSessionError(error)) {
      return Promise.reject(error);
    }

    // Initialize retry count if not present
    if (!config.__retryCount) {
      config.__retryCount = 0;
    }

    // Determine if we should retry
    const shouldRetry =
      (!error.response || // Network error
      (error.response.status >= 500 && error.response.status < 600) || // Server errors
      error.code === 'ECONNABORTED' || // Timeout
      error.code === 'NETWORK_ERROR') && // Connection issues
      error.response?.status !== 401 && // Never retry 401 Unauthorized
      error.response?.status !== 429; // Never retry 429 Too Many Requests (throttled auth endpoints)

    // Retry logic with exponential backoff
    if (shouldRetry && config.__retryCount < 3) {
      config.__retryCount++;

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, config.__retryCount) * 1000;
      console.log(`API Retry ${config.__retryCount}/3 for ${config.url} after ${delay}ms (Error: ${error.message})`);

      await new Promise(resolve => setTimeout(resolve, delay));
      return api(config);
    }

    // Log the final error after all retries
    console.error(`API Error after ${config.__retryCount} retries:`, {
      method: config.method?.toUpperCase(),
      url: config.url,
      status: error.response?.status,
      message: error.message,
      code: error.code
    });

    return Promise.reject(error);
  }
);

// Public auth endpoints (login itself stays in AuthContext with raw axios)
export const authAPI = {
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
};

// Buyers API
export const buyersAPI = {
  getAll: (params = {}) => api.get('/buyers', { params }),
  getById: (id) => api.get(`/buyers/${id}`),
  create: (data) => api.post('/buyers', data),
  update: (id, data) => api.put(`/buyers/${id}`, data),
  delete: (id) => api.delete(`/buyers/${id}`),
  bulkDelete: (buyer_ids) => api.post('/buyers/bulk-delete', { buyer_ids }),
  markDNC: (buyer_ids, reason) => api.post('/buyers/mark-dnc', { buyer_ids, reason }),
  removeDNC: (buyer_ids) => api.post('/buyers/remove-dnc', { buyer_ids }),
  getStatistics: () => api.get('/buyers/statistics'),
  getFilterOptions: () => api.get('/buyers/filters/options'),
  uploadCSV: (file, updateExisting = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('update_existing', updateExisting.toString());
    return api.post('/buyers/upload-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  downloadCSVTemplate: () => {
    return api.get('/buyers/csv-template', {
      responseType: 'blob',
    });
  },
  getCSVColumnsInfo: () => api.get('/buyers/csv-columns'),
  verifyEmail: (id, method = 'hunter') => api.post(`/buyers/${id}/verify-email`, { method }),
};

// Buyer Groups API
export const buyerGroupsAPI = {
  getAll: () => api.get('/buyer-groups'),
  getById: (id) => api.get(`/buyer-groups/${id}`),
  create: (data) => api.post('/buyer-groups', data),
  update: (id, data) => api.put(`/buyer-groups/${id}`, data),
  delete: (id) => api.delete(`/buyer-groups/${id}`),
  getMembers: (id, params = {}) => api.get(`/buyer-groups/${id}/members`, { params }),
  addMembers: (groupId, buyer_ids, added_by = null, notes = null) =>
    api.post(`/buyer-groups/${groupId}/members`, { buyer_ids, added_by, notes }),
  removeMembers: (groupId, buyer_ids) =>
    api.delete(`/buyer-groups/${groupId}/members`, { data: { buyer_ids } }),
  getBuyerGroups: (buyerId) => api.get(`/buyer-groups/buyers/${buyerId}/groups`),
  bulkAssign: (groupId, buyer_ids, added_by = null) =>
    api.post('/buyer-groups/bulk-assign', { group_id: groupId, buyer_ids, added_by }),
  bulkCreateAndAssign: (groupName, buyer_ids, description = '', color = '#3B82F6', added_by = null, industries = null) =>
    api.post('/buyer-groups/bulk-create-and-assign', {
      group_name: groupName,
      buyer_ids,
      description,
      color,
      added_by,
      industries
    }),
};

// Search API
export const searchAPI = {
  run: (data) => api.post('/search/run', data),
  getJobStatus: (jobId) => api.get(`/search/job/${jobId}`),
  getUserJobs: () => api.get('/search/jobs'),
  getStatus: () => api.get('/search/status'),
  getHistory: (limit = 20) => api.get('/search/history', { params: { limit } }),
};

export const researchAPI = {
  listProfiles: () => api.get('/research/profiles'),
  createProfile: (profile) => api.post('/research/profiles', profile),
  updateProfile: (id, profile) => api.put(`/research/profiles/${id}`, profile),
  setDefaultProfile: (id) => api.post(`/research/profiles/${id}/default`),
  deleteProfile: (id) => api.delete(`/research/profiles/${id}`),
  generateProfile: (brief) => api.post('/research/profiles/generate', { brief }, { timeout: 60000 }),
  run: ({ buyer_ids, profile_id, confirm }) => api.post('/research/run', { buyer_ids, profile_id, confirm }),
  forBuyer: (buyerId) => api.get(`/research/buyer/${buyerId}`),
  statuses: (buyerIds) => api.get('/research/status', { params: { buyer_ids: buyerIds.join(',') } }),
};

export const apiKeysAPI = {
  list: () => api.get('/keys'),
  create: ({ name, max_results_per_search }) => api.post('/keys', { name, max_results_per_search }),
  revoke: (id) => api.delete(`/keys/${id}`),
};

// Outreach API
export const outreachAPI = {
  sendEmail: (data) => api.post('/outreach/email', data),
  sendSMS: (data) => api.post('/outreach/sms', data),
  call: (data) => api.post('/outreach/call', data),
};

// Inbox API
export const inboxAPI = {
  getEmails: (buyerId) => api.get(`/inbox/conversations/${buyerId}/emails`),
  // Soft delete: hidden from the inbox, kept in history. A new reply brings the conversation back.
  deleteConversation: (buyerId) => api.delete(`/inbox/conversations/${buyerId}`),
  deleteMessage: (messageId) => api.delete(`/inbox/messages/${messageId}`),
};

// Settings API
export const settingsAPI = {
  getAll: async () => {
    const response = await api.get('/settings');
    return response.data;
  },
  update: async (settings) => {
    const response = await api.post('/settings', { settings });
    return response.data;
  },
  getResendWebhook: async () => {
    const response = await api.get('/settings/resend-webhook');
    return response.data;
  },
  get: async (key) => {
    const response = await api.get(`/settings/${key}`);
    return response.data;
  },
  delete: async (key) => {
    const response = await api.delete(`/settings/${key}`);
    return response.data;
  },
  validate: async (provider) => {
    const response = await api.post('/settings/validate', { provider });
    return response.data;
  },
  testSendGrid: async (email) => {
    const response = await api.post('/settings/test-sendgrid', { email });
    return response.data;
  },
  testSMTP: async () => {
    const response = await api.post('/settings/test-smtp');
    return response.data;
  },
  testResend: async (email) => {
    const response = await api.post('/settings/test-resend', { email });
    return response.data;
  },
  testTwilio: async () => {
    const response = await api.post('/settings/test-twilio');
    return response.data;
  },
  testAI: async () => {
    const response = await api.post('/settings/test-ai', {});
    return response.data;
  },
};

// Test API
export const testAPI = {
  email: (data) => api.post('/test/email', data),
  sms: (data) => api.post('/test/sms', data),
  previewEmail: (data) => api.post('/test/preview/email', data),
  previewSMS: (data) => api.post('/test/preview/sms', data),
};

// Templates API
export const templatesAPI = {
  getAll: (params = {}) => api.get('/templates', { params }),
  getById: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  createJson: (data) => api.post('/templates/json', data), // NEW: Create JSON template
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
  generate: (data) => api.post('/templates/generate', data),
  aiGenerateTemplate: (data) => api.post('/templates/ai/generate', data),
  aiGenerateSubject: (data) => api.post('/templates/ai/generate-subject', data),
  aiGenerateMessage: (data) => api.post('/templates/ai/generate-message', data),
  personalize: (data) => api.post('/templates/personalize', data),
  getPerformance: (id) => api.get(`/templates/${id}/performance`),
  getIndustryTemplates: (industry) => api.get(`/templates/industry/${industry}`),
  getByType: (type) => api.get(`/templates/type/${type}`),
};

// A/B Testing API
export const abTestingAPI = {
  getAllTests: (params = {}) => api.get('/ab-tests', { params }),
  getTest: (id) => api.get(`/ab-tests/${id}`),
  createTest: (data) => api.post('/ab-tests', data),
  updateTest: (id, data) => api.put(`/ab-tests/${id}`, data),
  deleteTest: (id) => api.delete(`/ab-tests/${id}`),
  startTest: (id) => api.post(`/ab-tests/${id}/start`),
  stopTest: (id) => api.post(`/ab-tests/${id}/stop`),
  getResults: (id) => api.get(`/ab-tests/${id}/results`),
  getSignificance: (id, metric) => api.get(`/ab-tests/${id}/significance`, { params: { metric } }),
  addVariant: (testId, data) => api.post(`/ab-tests/${testId}/variants`, data),
  updateVariant: (testId, variantId, data) => api.put(`/ab-tests/${testId}/variants/${variantId}`, data),
};

// Campaigns API - Template-Based System
export const campaignsAPI = {
  getAll: (params = {}) => api.get('/campaigns', { params }),
  getById: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),  // Unified creation endpoint
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
  launch: (id) => api.post(`/campaigns/${id}/launch`, { immediate: true }),  // Unified launch
  stop: (id) => api.post(`/campaigns/${id}/stop`, {}),
  duplicate: (id) => api.post(`/campaigns/${id}/duplicate`, {}),  // Duplicate campaign
  restart: (id) => api.post(`/campaigns/${id}/restart`, {}),  // Restart campaign
  sendTest: (id, testEmail) => api.post(`/campaigns/${id}/test`, { test_email: testEmail }),  // Send test campaign to email
  updateTemplateOverrides: (id, templateOverrides) => api.patch(`/campaigns/${id}/template-overrides`, { template_overrides: templateOverrides }),  // Update campaign-specific template customizations
  getStats: (id) => api.get(`/campaigns/${id}/stats`),
  bulkAction: (data) => api.post('/campaigns/bulk-action', data),
  getDeliveryFailures: (id, params = {}) => api.get(`/campaigns/${id}/delivery-failures`, { params }),
  // Live campaign control
  getLive: (id) => api.get(`/campaigns/${id}/live`),
  pause: (id) => api.post(`/campaigns/${id}/pause`, {}),
  resume: (id) => api.post(`/campaigns/${id}/resume`, {}),
  updateRecipients: (id, { add = [], remove = [] } = {}) =>
    api.patch(`/campaigns/${id}/recipients`, { add, remove }),
  updatePendingContent: (id, { messageIndex, subject, body }) =>
    api.patch(`/campaigns/${id}/pending-content`, { message_index: messageIndex, subject, body }),
  updateSchedule: (id, { messageIndex, delayHours }) =>
    api.patch(`/campaigns/${id}/schedule`, { message_index: messageIndex, delay_hours: delayHours }),
  testSendMessage: (campaignId, messageId, to) =>
    api.post(`/campaigns/${campaignId}/scheduled-messages/${messageId}/test-send`, to ? { to } : {}),
  retryMessage: (campaignId, messageId) =>
    api.post(`/campaigns/${campaignId}/scheduled-messages/${messageId}/retry`, {}),
  removeMessage: (campaignId, messageId, alsoSuppress = false) =>
    api.delete(`/campaigns/${campaignId}/scheduled-messages/${messageId}`, { data: { also_suppress: alsoSuppress } }),
};

// Responses API
export const responsesAPI = {
  getAll: (params = {}) => api.get('/responses', { params }),
  getById: (id) => api.get(`/responses/${id}`),
  parse: (data) => api.post('/responses/parse', data),
  categorize: (data) => api.post('/responses/categorize', data),
  getAnalytics: (params = {}) => api.get('/responses/analytics', { params }),
  getSentiment: (params = {}) => api.get('/responses/sentiment', { params }),
  getByCategory: (category) => api.get(`/responses/category/${category}`),
  getByTimeframe: (start, end) => api.get(`/responses/timeframe`, { params: { start, end } }),
  markProcessed: (id) => api.post(`/responses/${id}/processed`),
  addNote: (id, note) => api.post(`/responses/${id}/notes`, { note }),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: (params = {}) => api.get('/analytics/dashboard', { params }),
  getMetrics: (params = {}) => api.get('/analytics/metrics', { params }),
  getTrends: (metric, params = {}) => api.get(`/analytics/trends/${metric}`, { params }),
  getFunnel: (params = {}) => api.get('/analytics/funnels', { params }),
  getConversion: (params = {}) => api.get('/analytics/conversion', { params }),
  getPerformance: (params = {}) => api.get('/analytics/performance', { params }),
  getComparison: (params = {}) => api.get('/analytics/comparison', { params }),
  getTopPerformers: (metric, limit = 10) => api.get(`/analytics/top-performers/${metric}`, { params: { limit } }),
  export: (type, params = {}) => api.get(`/analytics/export/${type}`, { params }),
  getRealtime: () => api.get('/analytics/realtime'),
  // NEW: Analytics enhancement endpoints
  getRecentActivity: (limit = 10) => api.get('/analytics/recent-activity', { params: { limit } }),
  getProviderBreakdown: (params = {}) => api.get('/analytics/provider-breakdown', { params }),
  getTimeSeries: (metric, params = {}) => api.get('/analytics/time-series', { params: { metric, ...params } }),
  getProviderStats: (days = 30) => api.get('/analytics/provider-stats', { params: { days } }),
  getCampaignStats: () => api.get('/analytics/campaigns'),
};

// Performance API
export const performanceAPI = {
  getMetrics: () => api.get('/performance/metrics'),
  getHealth: () => api.get('/performance/health'),
  getDatabaseStats: () => api.get('/performance/database'),
  getCacheStats: () => api.get('/performance/cache'),
  getQueries: (params = {}) => api.get('/performance/queries', { params }),
  optimizeDatabase: () => api.post('/performance/optimize/database'),
  clearCache: (level) => api.post(`/performance/cache/clear/${level}`),
  getAlerts: () => api.get('/performance/alerts'),
  getBenchmark: () => api.get('/performance/benchmark'),
  getRecommendations: () => api.get('/performance/recommendations'),
  monitorSystem: () => api.get('/performance/monitor'),
};

// Admin API
export const adminAPI = {
  listTables: () => api.get('/admin/tables'),
  getRows: (table, params = {}) => api.get(`/admin/tables/${table}/rows`, { params }),
  getRow: (table, pk) => api.get(`/admin/tables/${table}/rows/${pk}`),
  updateRow: (table, pk, updates) => api.patch(`/admin/tables/${table}/rows`, { pk, updates }),
  deleteRow: (table, pk) => api.delete(`/admin/tables/${table}/rows/${pk}`),
  setUserPassword: (userId, password) => api.post(`/admin/users/${userId}/set-password`, { password }),
  getSearchStats: (limit = 50) => api.get('/admin/search-stats', { params: { limit } }),
  getStats: () => api.get('/admin/stats'),
  getSettingsEncryption: () => api.get('/admin/settings-encryption'),
  getCustomers: (params = {}) => api.get('/admin/customers', { params }),
  disableCustomer: (id) => api.post(`/admin/customers/${id}/disable`),
  enableCustomer: (id) => api.post(`/admin/customers/${id}/enable`),
  sendCustomerReset: (id) => api.post(`/admin/customers/${id}/send-reset`),
  viewAsCustomer: (id) => api.post(`/admin/customers/${id}/view-as`),
  exitViewAs: () => api.post('/admin/view-as/exit'),
  getAuditLog: (params = {}) => api.get('/admin/audit-log', { params }),
  resendSignupInvite: (payload) => api.post('/admin/resend-signup-invite', payload),
  setCustomerNote: (id, payload) => api.put(`/admin/customers/${id}/note`, payload),
};

// Lead Scoring API
export const leadScoringAPI = {
  getRules: () => api.get('/lead-scoring/rules'),
  createRule: (data) => api.post('/lead-scoring/rules', data),
  updateRule: (id, data) => api.put(`/lead-scoring/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/lead-scoring/rules/${id}`),
  scoreBuyer: (id) => api.get(`/lead-scoring/score/${id}`),
  scoreAll: () => api.post('/lead-scoring/score-all'),
  getScores: (params = {}) => api.get('/lead-scoring/scores', { params }),
  getAnalytics: () => api.get('/lead-scoring/analytics'),
  updateWeights: (data) => api.put('/lead-scoring/weights', data),
  recalculateAll: () => api.post('/lead-scoring/recalculate'),
};

// Personalization API
export const personalizationAPI = {
  getRules: () => api.get('/personalization/rules'),
  createRule: (data) => api.post('/personalization/rules', data),
  updateRule: (id, data) => api.put(`/personalization/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/personalization/rules/${id}`),
  analyzeBuyer: (id) => api.get(`/personalization/analyze/${id}`),
  getSegments: () => api.get('/personalization/segments'),
  createSegment: (data) => api.post('/personalization/segments', data),
  preview: (templateId, buyerId) => api.get(`/personalization/preview/${templateId}/${buyerId}`),
  getDynamicContent: (templateId, context) => api.post(`/personalization/dynamic/${templateId}`, { context }),
};

export const emailVerificationAPI = {
  // Background job approach (preferred) — returns immediately with a job_id
  startBulkVerify: (buyer_ids, method = 'hunter') => api.post('/email-verifications/bulk/start', { buyer_ids, method }),
  getJobStatus: (job_id) => api.get(`/email-verifications/job/${job_id}`),
  // Legacy synchronous endpoint kept for compatibility
  bulkVerify: (buyer_ids, method = 'hunter') => api.post('/email-verifications/bulk', { buyer_ids, method }, { timeout: 300000 }),
};

export const billingAPI = {
  getTokenBalance: () => api.get('/billing/tokens'),
  topUpTokens: (pkg) => api.post('/billing/tokens/topup', { package: pkg }),
};

export const plansAPI = {
  get: () => api.get('/billing/plans'),
};

export const creditsAPI = {
  getBalance: () => api.get('/billing/credits'),
  getHistory: (params = {}) => api.get('/billing/credits/history', { params }),
  estimateVerification: (buyer_ids) => api.post('/email-verifications/estimate', { buyer_ids }),
  adminLookup: (email) => api.get('/admin/credits/lookup', { params: { email } }),
  adminAdjust: (payload) => api.post('/admin/credits/adjust', payload),
  adminHistory: (userId, params = {}) => api.get(`/admin/credits/${userId}/history`, { params }),
};

// New-customer setup checklist (server-computed; booleans and fixed text only)
export const onboardingAPI = {
  get: () => api.get('/onboarding'),
  dismiss: (dismissed) => api.post('/onboarding/dismiss', { dismissed }),
};

export const domainHealthAPI = {
  check: (domain, options = {}) => api.post('/domain-health/check', {
    domain,
    provider: options.provider || null,
    custom_selector: options.customSelector || null,
  }, { timeout: 60000 }),
  getLatest: (domain) => api.get('/domain-health/latest', { params: { domain } }),
  getCheckedDomains: () => api.get('/domain-health'),
};

export const sendingIntelligenceAPI = {
  getRampState: (fromEmail) => api.get('/sending-ramp', { params: { from_email: fromEmail } }),
  enableRamp: (fromEmail) => api.post('/sending-ramp/enable', { from_email: fromEmail }),
  disableRamp: (fromEmail) => api.post('/sending-ramp/disable', { from_email: fromEmail }),
  getSendingPlan: (campaignId) => api.get(`/campaigns/${campaignId}/sending-plan`),
  getExcluded: (campaignId) => api.get(`/campaigns/${campaignId}/excluded`),
  getBreakdown: (campaignId) => api.get(`/campaigns/${campaignId}/sending-breakdown`),
  getHealth: (fromEmail) => api.get('/sending-health', { params: fromEmail ? { from_email: fromEmail } : {} }),
  getAdvice: (payload) => api.post('/sending-advice', payload),
};

export default api;
